import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/Logo';
import GlobalFooter from '../components/GlobalFooter';
import {
  ArrowLeft, Sun, Pill, AlertTriangle, Clock, Calendar,
  Shield, Brain, Activity, Heart, Eye, Zap, CheckCircle2,
  ChevronDown, ChevronUp, Info, Utensils, Dumbbell, Thermometer,
  BookOpen, FlaskConical, Stethoscope
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '../components/ui/accordion';

const MS_PROTOCOL = [
  {
    name: 'Vitamin D3 (High-Dose)',
    dosage: '5,000-10,000 IU daily',
    timing: 'Morning with breakfast (fat-containing meal)',
    icon: Sun,
    color: 'bg-amber-100 text-amber-700 border-amber-200',
    evidence: 'A',
    why: 'MS prevalence strongly correlates with low Vitamin D levels. Multiple large-scale studies show high-dose D3 may reduce relapse rates by 50-70% and slow disability progression. The SOLAR and CHOLINE trials demonstrated significant benefits.',
    interactions: ['Monitor calcium levels with high doses', 'Safe with most DMTs', 'Enhances corticosteroid effectiveness'],
    notes: 'Get levels tested (aim for 60-80 ng/mL). Most MS specialists now recommend high-dose D3.'
  },
  {
    name: 'Omega-3 (High EPA/DHA)',
    dosage: '2,000-4,000mg combined EPA/DHA',
    timing: 'With meals containing fat, split AM/PM',
    icon: Heart,
    color: 'bg-rose-100 text-rose-700 border-rose-200',
    evidence: 'A',
    why: 'Omega-3s reduce neuroinflammation, a key driver of MS progression. EPA specifically modulates immune cell activity. Studies show improved fatigue scores and potentially reduced brain atrophy rates.',
    interactions: ['May enhance blood thinners — monitor if on anticoagulants', 'Safe with all standard DMTs', 'Pairs well with Vitamin D3'],
    notes: 'Choose high-quality, third-party tested fish oil (IFOS certified). Algae-based for fish allergy.'
  },
  {
    name: 'Vitamin B12 (Methylcobalamin)',
    dosage: '1,000-5,000mcg sublingual',
    timing: 'Morning, under the tongue for direct absorption',
    icon: Zap,
    color: 'bg-violet-100 text-violet-700 border-violet-200',
    evidence: 'A',
    why: 'B12 is essential for myelin production and nerve repair — the exact processes damaged in MS. Deficiency mimics MS symptoms and worsens existing ones. Methylcobalamin form directly supports methylation needed for myelin repair.',
    interactions: ['Safe with all DMTs', 'Metformin depletes B12 — extra important if diabetic', 'Pairs well with folate for methylation support'],
    notes: 'Sublingual form bypasses gut absorption issues common in MS. Get B12 levels checked regularly.'
  },
  {
    name: 'Alpha-Lipoic Acid (ALA)',
    dosage: '600-1,200mg daily',
    timing: 'On an empty stomach, 30 min before meals',
    icon: Shield,
    color: 'bg-green-100 text-green-700 border-green-200',
    evidence: 'B+',
    why: 'ALA is both water and fat-soluble, meaning it can cross the blood-brain barrier to provide antioxidant protection directly to neurons. Clinical trials show it may reduce brain atrophy by 68% compared to placebo over 2 years.',
    interactions: ['May lower blood sugar — monitor if diabetic', 'Safe with standard DMTs', 'May enhance effects of insulin/diabetes meds'],
    notes: 'R-Alpha Lipoic Acid (R-ALA) is the bioactive form with better absorption. Start with 600mg.'
  },
  {
    name: 'High-Dose Biotin (MD1003)',
    dosage: '100mg, 3 times daily (300mg total)',
    timing: 'With meals, spaced evenly throughout the day',
    icon: Brain,
    color: 'bg-purple-100 text-purple-700 border-purple-200',
    evidence: 'B',
    why: 'High-dose biotin (NOT regular biotin) activates enzymes involved in myelin repair and energy production in nerve cells. The MS-SPI trial showed 12.6% of progressive MS patients improved on MD1003 vs 0% on placebo.',
    interactions: ['IMPORTANT: Interferes with lab tests (thyroid, troponin, hormones) — stop 3-5 days before blood work', 'Safe with DMTs', 'Much higher dose than regular biotin supplements'],
    notes: 'This is specifically for progressive MS. Regular biotin (5-10mg) is NOT the same. Discuss with neurologist.'
  },
  {
    name: 'Magnesium Glycinate',
    dosage: '300-400mg elemental magnesium',
    timing: 'Evening, 1-2 hours before bed',
    icon: Activity,
    color: 'bg-cyan-100 text-cyan-700 border-cyan-200',
    evidence: 'B+',
    why: 'Magnesium supports nerve signal transmission and muscle function — both impaired in MS. Reduces spasticity, muscle cramps, and sleep disturbances. Most MS patients are deficient. Glycinate form is calming and well-absorbed.',
    interactions: ['Separate from antibiotics by 2+ hours', 'Safe with all DMTs', 'May enhance muscle relaxants — discuss with doctor'],
    notes: 'Glycinate form avoids the digestive issues of other forms. Helps with MS fatigue and spasticity.'
  },
  {
    name: 'CoQ10 (Ubiquinol)',
    dosage: '200-400mg daily',
    timing: 'With breakfast or lunch (fat-containing meal)',
    icon: Zap,
    color: 'bg-orange-100 text-orange-700 border-orange-200',
    evidence: 'B',
    why: 'CoQ10 supports mitochondrial energy production in nerve cells. MS involves mitochondrial dysfunction. Studies show reduced fatigue and depression scores. Also protects against oxidative damage to myelin.',
    interactions: ['May reduce effectiveness of blood thinners (warfarin)', 'Safe with DMTs', 'Statin drugs deplete CoQ10 — extra important if taking statins'],
    notes: 'Ubiquinol form (not ubiquinone) is pre-converted and better absorbed, especially over age 40.'
  },
  {
    name: 'Probiotic (Multi-Strain)',
    dosage: '30-50 billion CFU daily',
    timing: 'Morning on empty stomach, 30 min before food',
    icon: FlaskConical,
    color: 'bg-teal-100 text-teal-700 border-teal-200',
    evidence: 'B',
    why: 'The gut-brain axis plays a significant role in MS. MS patients have altered gut microbiomes. Probiotics may help regulate immune responses that drive MS attacks. Studies show improvements in inflammatory markers and mental health scores.',
    interactions: ['Safe with all DMTs', 'Immunosuppressants may reduce effectiveness', 'Take separately from antibiotics (2+ hours apart)'],
    notes: 'Look for strains: L. rhamnosus, B. longum, L. plantarum. Refrigerated brands maintain potency better.'
  }
];

const COMMON_DMTS = [
  { name: 'Ocrevus (ocrelizumab)', notes: 'All 8 supplements are generally safe. Monitor Vitamin D levels closely as B-cell depletion may affect vitamin metabolism.' },
  { name: 'Tecfidera (dimethyl fumarate)', notes: 'Safe with all supplements. Tecfidera may cause GI issues — probiotics and magnesium can actually help. Take supplements 2 hours apart from Tecfidera.' },
  { name: 'Tysabri (natalizumab)', notes: 'All supplements are safe. Vitamin D is especially important as Tysabri patients may have altered vitamin D metabolism.' },
  { name: 'Copaxone (glatiramer)', notes: 'No known interactions with any of these supplements. One of the safest DMTs for supplement use.' },
  { name: 'Aubagio (teriflunomide)', notes: 'Monitor liver function — Alpha-Lipoic Acid supports liver health. CoQ10 may help with Aubagio-related fatigue.' },
  { name: 'Kesimpta (ofatumumab)', notes: 'Similar safety profile to Ocrevus. All supplements are generally safe. Prioritize Vitamin D and probiotics.' },
];

const LIFESTYLE_TIPS = [
  {
    icon: Utensils,
    title: 'Anti-Inflammatory Diet',
    content: 'Focus on the Wahls Protocol or Mediterranean diet: leafy greens (3 cups/day), colorful vegetables (3 cups/day), omega-3 rich foods, and sulfur-rich vegetables (onions, garlic, mushrooms). Minimize processed foods, refined sugar, and dairy.'
  },
  {
    icon: Dumbbell,
    title: 'Exercise & Movement',
    content: 'Regular exercise reduces fatigue and improves mobility. Aim for 150 min/week of moderate activity. Swimming and aquatic exercises are ideal (water supports balance and keeps you cool). Yoga and tai chi help with balance and spasticity.'
  },
  {
    icon: Thermometer,
    title: 'Heat Management',
    content: "Uhthoff's phenomenon — heat temporarily worsens MS symptoms. Use cooling vests, schedule outdoor activities for cooler parts of the day, take lukewarm showers, and stay hydrated. Symptoms resolve when body temperature normalizes."
  },
  {
    icon: Brain,
    title: 'Cognitive Strategies',
    content: 'MS cognitive fog is real and manageable. Use timers, calendars, and notes apps consistently. Break tasks into smaller steps. Do challenging mental work during your peak energy hours. Brain-training apps can help maintain cognitive reserve.'
  },
  {
    icon: Activity,
    title: 'Fatigue Management',
    content: 'MS fatigue is the #1 reported symptom. Prioritize sleep hygiene, pace activities with rest breaks, use energy conservation techniques, and consider your \"energy budget\" for each day. Vitamin D and CoQ10 specifically target MS-related fatigue.'
  }
];

const EvidenceGrade = ({ grade }) => {
  const colors = {
    'A': 'bg-green-100 text-green-800 border-green-300',
    'B+': 'bg-blue-100 text-blue-800 border-blue-300',
    'B': 'bg-sky-100 text-sky-800 border-sky-300'
  };
  return (
    <span className={`px-2 py-0.5 text-xs font-bold rounded-full border ${colors[grade] || colors['B']}`} data-testid={`evidence-grade-${grade}`}>
      Evidence: {grade}
    </span>
  );
};

export default function MSGuidePage() {
  const { user } = useAuth();

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex flex-col">
      {/* Header */}
      <header className="border-b border-border bg-white/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/dashboard" className="flex items-center gap-2 text-muted-foreground hover:text-primary" data-testid="ms-guide-back">
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </Link>
          <Logo size="small" linkTo="/" />
          <div className="w-16" />
        </div>
      </header>

      <main className="flex-1 max-w-4xl mx-auto px-4 sm:px-6 py-8 w-full" data-testid="ms-guide-page">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-8 h-8 text-purple-600" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#1A1A1A] mb-3">
            MS Quick Start Guide
          </h1>
          <p className="text-muted-foreground max-w-lg mx-auto text-sm sm:text-base">
            An evidence-based supplement protocol and lifestyle guide specifically designed for people living with Multiple Sclerosis.
          </p>
        </motion.div>

        {/* Disclaimer */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-amber-50 border border-amber-200 rounded-2xl p-5 mb-8"
          data-testid="ms-guide-disclaimer"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-semibold text-amber-900 mb-1">Important Medical Disclaimer</p>
              <p className="text-xs text-amber-800">
                This guide is for educational purposes only and does not replace medical advice. Always discuss supplement changes with your neurologist or MS specialist, 
                especially regarding interactions with your Disease-Modifying Therapy (DMT). Supplements support — but do not replace — your prescribed treatment plan.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Supplement Protocol */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-10"
        >
          <h2 className="text-xl font-bold text-[#1A1A1A] mb-2 flex items-center gap-2">
            <Pill className="w-5 h-5 text-primary" />
            MS Supplement Protocol
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            8 evidence-based supplements ranked by research strength. Start with the "A" rated essentials, then add others based on your needs.
          </p>

          {/* Daily Schedule Overview */}
          <div className="bg-white rounded-2xl border border-border p-5 mb-6" data-testid="daily-schedule">
            <h3 className="font-semibold text-[#1A1A1A] mb-4 flex items-center gap-2">
              <Clock className="w-4 h-4 text-primary" />
              Optimized Daily Schedule
            </h3>
            <div className="space-y-3">
              {[
                { time: 'Morning (with breakfast)', supps: 'Vitamin D3, Omega-3, B12 (sublingual), CoQ10, Probiotic (30 min before)', color: 'bg-amber-50 border-amber-200 text-amber-900' },
                { time: 'Midday (with lunch)', supps: 'Biotin (2nd dose), Omega-3 (2nd dose)', color: 'bg-sky-50 border-sky-200 text-sky-900' },
                { time: 'Afternoon (empty stomach)', supps: 'Alpha-Lipoic Acid (30 min before dinner)', color: 'bg-orange-50 border-orange-200 text-orange-900' },
                { time: 'Evening (before bed)', supps: 'Magnesium Glycinate, Biotin (3rd dose with dinner)', color: 'bg-indigo-50 border-indigo-200 text-indigo-900' }
              ].map((slot, i) => (
                <div key={i} className={`rounded-xl border p-3 ${slot.color}`}>
                  <p className="text-xs font-semibold">{slot.time}</p>
                  <p className="text-xs mt-1">{slot.supps}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Supplement Cards */}
          <div className="space-y-4">
            <Accordion type="multiple" className="space-y-3">
              {MS_PROTOCOL.map((supp, index) => {
                const Icon = supp.icon;
                return (
                  <motion.div
                    key={supp.name}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.05 * index }}
                  >
                    <AccordionItem value={supp.name} className="bg-white rounded-xl border border-border overflow-hidden">
                      <AccordionTrigger className="px-4 py-3 hover:no-underline hover:bg-gray-50/50">
                        <div className="flex items-center gap-3 text-left">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center border ${supp.color}`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-semibold text-sm text-[#1A1A1A]">{supp.name}</h3>
                              <EvidenceGrade grade={supp.evidence} />
                            </div>
                            <div className="flex gap-3 mt-1 text-xs text-muted-foreground">
                              <span>{supp.dosage}</span>
                              <span>{supp.timing}</span>
                            </div>
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-4">
                        <div className="space-y-4 pt-2">
                          <div className="p-3 rounded-lg bg-purple-50 border border-purple-200">
                            <h4 className="text-sm font-medium text-purple-900 mb-1 flex items-center gap-2">
                              <FlaskConical className="w-4 h-4" />
                              Why This Matters for MS
                            </h4>
                            <p className="text-xs text-purple-800">{supp.why}</p>
                          </div>

                          {supp.interactions.length > 0 && (
                            <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                              <h4 className="text-sm font-medium text-blue-900 mb-2 flex items-center gap-2">
                                <Shield className="w-4 h-4" />
                                DMT Interactions
                              </h4>
                              <ul className="space-y-1">
                                {supp.interactions.map((int_, i) => (
                                  <li key={i} className="text-xs text-blue-800 flex items-start gap-2">
                                    <CheckCircle2 className="w-3 h-3 mt-0.5 flex-shrink-0" />
                                    {int_}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {supp.notes && (
                            <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
                              <h4 className="text-sm font-medium text-gray-900 mb-1 flex items-center gap-2">
                                <Info className="w-4 h-4" />
                                Clinical Note
                              </h4>
                              <p className="text-xs text-gray-700">{supp.notes}</p>
                            </div>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </motion.div>
                );
              })}
            </Accordion>
          </div>
        </motion.div>

        {/* DMT Compatibility */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15 }}
          className="mb-10"
        >
          <h2 className="text-xl font-bold text-[#1A1A1A] mb-2 flex items-center gap-2">
            <Stethoscope className="w-5 h-5 text-primary" />
            DMT Compatibility Guide
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            How these supplements interact with common Disease-Modifying Therapies.
          </p>
          <div className="space-y-3" data-testid="dmt-compatibility">
            {COMMON_DMTS.map((dmt, i) => (
              <div key={i} className="bg-white rounded-xl border border-border p-4">
                <h4 className="font-semibold text-sm text-[#1A1A1A] mb-1">{dmt.name}</h4>
                <p className="text-xs text-muted-foreground">{dmt.notes}</p>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Lifestyle Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="mb-10"
        >
          <h2 className="text-xl font-bold text-[#1A1A1A] mb-2 flex items-center gap-2">
            <Heart className="w-5 h-5 text-primary" />
            MS Lifestyle Strategies
          </h2>
          <p className="text-sm text-muted-foreground mb-6">
            Evidence-based lifestyle modifications that complement your supplement protocol.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="lifestyle-tips">
            {LIFESTYLE_TIPS.map((tip, i) => {
              const Icon = tip.icon;
              return (
                <div key={i} className="bg-white rounded-xl border border-border p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-primary" />
                    </div>
                    <h3 className="font-semibold text-sm text-[#1A1A1A]">{tip.title}</h3>
                  </div>
                  <p className="text-xs text-muted-foreground leading-relaxed">{tip.content}</p>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* MS Symptom Tracker CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.25 }}
          className="bg-gradient-to-br from-purple-50 to-indigo-50 border border-purple-200 rounded-2xl p-6 mb-10 text-center"
          data-testid="symptom-tracker-cta"
        >
          <h3 className="text-lg font-bold text-purple-900 mb-2">Track Your MS Symptoms</h3>
          <p className="text-sm text-purple-700 mb-4 max-w-md mx-auto">
            Log fatigue, numbness, cognitive fog, spasticity, and other symptoms daily to identify patterns and share with your neurologist.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button asChild className="btn-primary">
              <Link to="/symptoms">Start Symptom Tracking</Link>
            </Button>
            <Button asChild variant="outline">
              <Link to="/journal">Open Journal</Link>
            </Button>
          </div>
        </motion.div>

        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="grid grid-cols-2 gap-3 mb-10"
        >
          <Link
            to="/quiz"
            className="bg-white rounded-xl border border-border p-4 text-center hover:border-primary/40 transition-colors"
          >
            <Pill className="w-6 h-6 text-primary mx-auto mb-2" />
            <p className="text-sm font-medium text-[#1A1A1A]">Get Personalized Protocol</p>
            <p className="text-xs text-muted-foreground mt-1">Take the full assessment</p>
          </Link>
          <Link
            to="/ask"
            className="bg-white rounded-xl border border-border p-4 text-center hover:border-primary/40 transition-colors"
          >
            <Brain className="w-6 h-6 text-primary mx-auto mb-2" />
            <p className="text-sm font-medium text-[#1A1A1A]">Ask AI About MS</p>
            <p className="text-xs text-muted-foreground mt-1">Get supplement answers</p>
          </Link>
        </motion.div>
      </main>

      <GlobalFooter />
    </div>
  );
}
